/**
 * @file Cost Type Help Content
 * @description Static help content for each cost type displayed in the help modal.
 * Provides descriptions, examples, field explanations, and tips to guide users
 * when entering cost data in the Add Cost dialog.
 */

import type { CostType } from '@medsuite/models';

export interface CostTypeHelpContent {
  /** Display title for the cost type */
  title: string;
  /** What this cost type represents */
  description: string;
  /** 2-3 example use cases */
  examples: string[];
  /** Fields explanation */
  fields: {
    /** Standard fields applicable to this cost type */
    standard: string[];
    /** Type-specific fields (if any) */
    specific?: string[];
  };
  /** Optional guidance tip */
  tips?: string;
}

export const costTypeHelpContent: Record<CostType, CostTypeHelpContent> = {
  software: {
    title: 'Software/Tools/Licenses',
    description:
      'Costs for software systems, tools, SaaS subscriptions, and software licenses required for the investment. This includes both one-time purchases and recurring subscription fees.',
    examples: [
      'SaaS subscription fees (e.g., Salesforce, ServiceNow, Jira)',
      'Software license purchases or renewals',
      'Development tools and IDE licenses',
    ],
    fields: {
      standard: [
        'Amount: The cost per billing period (monthly, annually, etc.)',
        'Frequency: How often the cost recurs (one-time, monthly, annually)',
        'CapEx/OpEx: Capital expenditure (one-time purchase) or Operating expenditure (recurring)',
      ],
    },
    tips: 'For annual subscriptions billed once per year, use "Annually" frequency with the full annual amount. The system will calculate the monthly equivalent automatically.',
  },

  contractor: {
    title: 'Contractor/External Labor',
    description:
      'Costs for external consultants, contractors, or staff augmentation engaged through statements of work (SOW) or time-and-materials contracts. These are non-employee resources brought in to support the investment.',
    examples: [
      'Implementation consultants for a new system rollout',
      'External developers for custom integration work',
      'Subject matter experts for compliance or audit support',
    ],
    fields: {
      standard: [
        'Frequency: Duration of engagement (weekly, monthly, etc.)',
        'Start/End Date: Engagement period for the contractor',
      ],
      specific: [
        'Annual Salary: Use for fixed-rate contractors with an annualized equivalent',
        'Hourly Rate + Hours/Week: Use for time-and-materials engagements',
      ],
    },
    tips: 'Enter either an annual salary equivalent OR an hourly rate with hours per week. The system calculates monthly costs automatically. For project-based work, estimate total hours and divide across the engagement period.',
  },

  employee: {
    title: 'Employee/Headcount',
    description:
      'Costs for internal full-time employees (FTEs) dedicated to the investment. This includes new hires or allocation of existing employees whose time will be charged to the project.',
    examples: [
      'New hire: Project Manager dedicated to the initiative',
      'Existing staff: Engineers allocated 50% to the project',
      'Temporary internal transfer for implementation support',
    ],
    fields: {
      standard: [
        'Frequency: Duration of the allocation',
        'Start/End Date: When the employee is assigned to this investment',
      ],
      specific: [
        'Annual Salary: Fully loaded salary (base + benefits + overhead)',
        'Hourly Rate + Hours/Week: For partial allocations or hourly tracking',
      ],
    },
    tips: 'For partial allocations, use the hourly rate with actual hours per week dedicated to this investment. Include benefits and overhead in the salary for accurate fully-loaded cost.',
  },

  service: {
    title: 'Services',
    description:
      'Costs for operational services consumed by the business as part of the investment. This includes support contracts, managed services, professional services, and other recurring service fees.',
    examples: [
      'Annual support and maintenance contracts',
      'Managed service provider (MSP) fees',
      'Professional services for training or change management',
    ],
    fields: {
      standard: [
        'Amount: The service cost per billing period',
        'Frequency: How often the service is billed (monthly, quarterly, annually)',
        'CapEx/OpEx: Typically OpEx for ongoing services',
      ],
    },
    tips: 'Service contracts often have annual terms with monthly or quarterly billing. Match the frequency to how you are actually invoiced to track costs accurately.',
  },

  audit: {
    title: 'Audit/Compliance',
    description:
      'Costs for security audits, compliance assessments, certifications, and regulatory reviews required by the investment. These are often one-time or annual costs tied to specific compliance requirements.',
    examples: [
      'SOC 2 Type II audit and certification',
      'HIPAA compliance assessment',
      'Penetration testing and security review',
    ],
    fields: {
      standard: [
        'Amount: The audit or assessment cost',
        'Frequency: Usually one-time or annually for recurring certifications',
        'CapEx/OpEx: Typically OpEx for compliance activities',
      ],
    },
    tips: 'Many compliance certifications require annual renewal. Plan for recurring audit costs if the investment has ongoing compliance obligations.',
  },

  infrastructure: {
    title: 'Infrastructure/Hosting',
    description:
      'Costs for cloud hosting, infrastructure, platform services, and technology spend. This includes compute, storage, networking, and platform-as-a-service (PaaS) costs.',
    examples: [
      'Cloud hosting (AWS, Azure, GCP) compute and storage',
      'Database-as-a-service (e.g., RDS, CosmosDB)',
      'CDN, networking, and data transfer costs',
    ],
    fields: {
      standard: [
        'Amount: Monthly or annual infrastructure spend',
        'Frequency: How often you are billed (typically monthly)',
        'CapEx/OpEx: Usually OpEx for cloud services',
      ],
    },
    tips: 'Cloud costs can vary. Use your estimated average monthly spend. Consider including a buffer for usage growth. Review actual costs quarterly and adjust if needed.',
  },

  other: {
    title: 'Other',
    description:
      'Miscellaneous costs that do not fit into the standard categories. Use this for unique or one-off expenses specific to your investment.',
    examples: [
      'Travel expenses for implementation team',
      'Hardware purchases (if not covered elsewhere)',
      'Contingency or buffer for unforeseen costs',
    ],
    fields: {
      standard: [
        'Amount: The cost amount per period',
        'Frequency: How often this cost occurs',
        'CapEx/OpEx: Select based on the nature of the expense',
      ],
    },
    tips: 'Provide clear details in the Cost Details field so reviewers understand what this cost covers. Consider whether a more specific cost type might be appropriate.',
  },
};
